import React, { useMemo } from 'react';
import PropTypes, { element } from 'prop-types';
import {
  getProfilePicUrl,
  getUsername,
} from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import {
  ContactImage,
  NewHC200Icon,
  UnassignedHS200DeviceIcon,
  VideoCallUnreachableIcon,
} from './Icons';
import { Tooltip } from 'antd';
import { PhotoResolution, VideoCallStatus } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { useSelector } from 'react-redux';
import { getProfilePics } from '@vantiva-sa/homesight_humaui/Store/dataStore';
import '../../styles/NewContact.less';
import { useVideoCallStatus } from '../VideoCall/useVideoCallStatus';

const NewContact = ({
  className,
  contact,
  actionIcon,
  onClick,
  renderDetails = [],
  showRelationship = true,
  defaultName = '',
  hubRecord = {},
  starteventAction = false,
  assignstaff = false, // New prop to determine staffrole display
  principalRecord = {},
}) => {
  const statusTranslations = useVideoCallStatus();
  const name = getUsername(contact, { defaultName });
  const truncatedName = name.length > 20 ? name.slice(0, 20) + '...' : name;

  const resourcesMap = useSelector(getProfilePics());
  const url = getProfilePicUrl(contact.useruuid, resourcesMap, PhotoResolution.R64);
  const image = (contact?.profilePics && contact?.profilePics[PhotoResolution.R64]) ?? url;

  // Show staffrole only if assignstaff is true, otherwise keep the existing relationship logic
  const roleToDisplay = useMemo(() => {
    if (assignstaff) {
      return contact?.staffrole;
    }
    if (contact?.useruuid === principalRecord?.useruuid) {
      return principalRecord?.relationship;
    }
    return contact?.targetrelationship;
  }, [assignstaff, contact, hubRecord]);

  const isInCall = renderDetails.some((renderItem) => {
    const element = renderItem(contact);
    const children = element?.props?.children;
    const statusText = Array.isArray(children) ? children[0] : children;
    return statusText === 'In Call';
  });

  return (
    <div
      className={`new-contact-wrapper ${className ?? ''} ${
        onClick ? 'new-clickable-item' : ''
      } ${isInCall ? 'new-contact-wrapper-in-call-background' : ''}`}
      onClick={() => onClick && onClick(contact)}
    >
      <div className="new-contact-item-content-container">
        <div className="new-contact-item-left-container">
          <div className="new-contact-item-photo-container">
            {image ? (
              <img src={image} className="user-profile-pic user-profile-image" alt="pic" />
            ) : (
              <ContactImage className="photo-container" />
            )}
          </div>
          <div className="new-contact-item-information">
            <Tooltip
              title={name}
              className={`new-contact-item-name ${isInCall ? 'new-contact-item-name-in-call' : ''}`}
            >
              {truncatedName}
            </Tooltip>
            {showRelationship && (
              <span
                className={`new-contact-item-role ${isInCall ? 'new-contact-item-role-in-call' : ''}`}
              >
                {roleToDisplay || ' '}
              </span>
            )}
            <span className="new-contact-item-calling-status">
              {renderDetails
                .map((renderItem) => renderItem(contact))
                .filter((element) => {
                  const children = element?.props?.children;
                  const statusText = Array.isArray(children) ? children[0] : null;
                  return statusText !== 'In Call';
                })
                .map((element) => {
                  const children = element?.props?.children;
                  const statusText = Array.isArray(children) ? children[0] : children;
                  // Find the key in VideoCallStatus that matches the current status
                  const statusKey = Object.keys(VideoCallStatus).find(
                    (key) => VideoCallStatus[key].toLowerCase() === statusText?.toLowerCase()
                  );
                  return (
                    <span className="new-contact-item-calling-status" key={element.key}>
                      {React.cloneElement(element, {
                        children: React.Children.map(element.props.children, (child) => {
                          if (typeof child === 'string') {
                            // Replace the string (e.g., "Unreachable") with the translated version
                            const statusKey = Object.keys(VideoCallStatus).find(
                              (key) => VideoCallStatus[key].toLowerCase() === child.toLowerCase()
                            );
                            const translated = statusKey ? statusTranslations[statusKey] : child;
                            return <span className="translated-status-text">{translated}</span>;
                          }
                          return child;
                        }),
                      })}
                    </span>
                  );
                })}
            </span>
          </div>
        </div>
        <div className="new-contact-right-container">
          {!starteventAction && (
            <span style={{ width: '60px' }}>
              {(contact?.useruuid === hubRecord?.useruuid && hubRecord?.deviceconnectstatus) ||
              contact?.deviceuuid != null ? (
                <div className="new-contact-device-status-container">
                  <NewHC200Icon
                    status={
                      contact?.useruuid === hubRecord?.useruuid
                        ? hubRecord.deviceconnectstatus?.toLowerCase()
                        : contact.deviceconnectstatus?.toLowerCase()
                    }
                    className="device-assigned"
                  />
                </div>
              ) : contact?.usertype === 'Principal' ? (
                <UnassignedHS200DeviceIcon className="device-unassigned" />
              ) : null}
            </span>
          )}
          {renderDetails.map((renderItem) => {
            const element = renderItem(contact);
            const children = element?.props?.children;
            const statusText = Array.isArray(children) ? children[0] : null;
            const clickableStatuses = ['Unreachable', 'Missed'];
            const disabledStatuses = ['Calling', 'Joining'];
            const showUnreachableIcon = [...clickableStatuses, ...disabledStatuses].includes(
              statusText
            );
            const isClickable = clickableStatuses.includes(statusText);
            const isDisabled = disabledStatuses.includes(statusText);
            return (
              <span
                className="status-line"
                key={element.key}
                style={{
                  cursor: isDisabled ? 'not-allowed' : isClickable ? 'pointer' : 'default',
                }}
              >
                {showUnreachableIcon && (
                  <VideoCallUnreachableIcon
                    className="status-icon"
                    onClick={isClickable ? () => {} : undefined}
                    style={{
                      pointerEvents: isDisabled ? 'none' : 'auto',
                    }}
                  />
                )}
              </span>
            );
          })}
          {actionIcon && <span className="new-contact-item-action-icon-wrapper">{actionIcon}</span>}
        </div>
      </div>
    </div>
  );
};

NewContact.defaultProps = {
  contact: {},
};

NewContact.propTypes = {
  contact: PropTypes.object,
  assignstaff: PropTypes.bool,
  className: PropTypes.string,
  actionIcon: PropTypes.node,
  onClick: PropTypes.func,
  renderDetails: PropTypes.array,
  showRelationship: PropTypes.bool,
  defaultName: PropTypes.string,
  hubRecord: PropTypes.object,
  starteventAction: PropTypes.bool,
  linkAccountChecked: PropTypes.bool,
  principalRecord: PropTypes.object,
};

export default NewContact;
